import { useState } from 'react';
import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PolicyRoute, PolicyRouteOption, routeKey } from './policy';

export function PolicyRouteEditor({
  options,
  inherited,
  selected,
  excluded,
  extend,
  disabled,
  change,
}: {
  options: PolicyRouteOption[];
  inherited: PolicyRoute[];
  selected: PolicyRoute[];
  excluded: PolicyRoute[];
  extend: boolean;
  disabled: boolean;
  change(routes: PolicyRoute[], excluded: PolicyRoute[]): void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const rows = [
    ...options,
    ...[...selected, ...excluded]
      .filter((r) => !options.some((o) => routeKey(o) === routeKey(r)))
      .map((r) => ({ ...r, display_name: r.connector_account_id, access_display_name: null })),
  ];
  const unique = [...new Map(rows.map((r) => [routeKey(r), r])).values()];
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <TextField label={t('hotelPolicy.findProvider')} value={query} onChange={(e) => setQuery(e.target.value)} />
      {unique
        .filter((r) =>
          `${r.access_display_name ?? ''} ${r.display_name} ${r.access_code}`
            .toLocaleLowerCase()
            .includes(query.toLocaleLowerCase()),
        )
        .map((r) => {
          const key = routeKey(r),
            active = options.some((o) => routeKey(o) === key);
          const fromParent = inherited.some((o) => routeKey(o) === key);
          const added = selected.some((o) => routeKey(o) === key),
            removed = excluded.some((o) => routeKey(o) === key);
          const value = added ? 'add' : removed ? 'exclude' : extend && fromParent ? 'keep' : 'off';
          // Retain explicit additions/exclusions, even if redundant, until the administrator changes them.
          const choices = extend ? ['keep', 'add', 'exclude'] : ['off', 'add'];
          if (extend && !fromParent) choices[0] = 'off';
          return (
            <Stack
              key={key}
              direction={{ xs: 'column', sm: 'row' }}
              gap={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider', minWidth: 0 }}
            >
              <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                <Typography>
                  {r.access_display_name || t('hotelPolicy.accessIdentity', { code: r.access_code })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.display_name} · {r.access_code}
                </Typography>
                {extend && fromParent && (
                  <Typography variant="caption" display="block">
                    {t('hotelPolicy.inheritedProvider')}
                  </Typography>
                )}
                {!active && (
                  <Typography color="error" variant="caption" display="block">
                    {t('hotelPolicy.unavailableRoute')}
                  </Typography>
                )}
              </Box>
              <TextField
                select
                size="small"
                label={t('hotelPolicy.providerAction')}
                value={value}
                disabled={disabled}
                sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}
                onChange={(e) => {
                  const next = e.target.value;
                  const routes = selected.filter((o) => routeKey(o) !== key),
                    exclusions = excluded.filter((o) => routeKey(o) !== key);
                  const identity = { connector_account_id: r.connector_account_id, access_code: r.access_code };
                  if (next === 'add') routes.push(identity);
                  if (next === 'exclude') exclusions.push(identity);
                  change(routes, exclusions);
                }}
              >
                {choices.map((v) => (
                  <MenuItem key={v} value={v}>
                    {t(`hotelPolicy.action_${v}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          );
        })}
    </Stack>
  );
}
